'use client';

import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState, AppDispatch } from '@/lib/store';
import { fetchActiveProjects } from '@/lib/store/slices/projectSlice';
import { useIntersection, revealClasses } from '@/lib/hooks/useIntersection';
import { Pagination } from '@/components/ui/Pagination';
import { FilterBar } from '@/components/sections/FilterBar';
import CategoryChips from '@/components/listing/CategoryChips';
import type { ListingHero } from '@/components/listing/types';
import { SkeletonCard } from '@/components/ui/Skeleton';
import { ProjectCard } from '@/components/ui/ProjectCard';
import { useTranslations } from 'next-intl';
import type { FilterValue } from '@/components/sections/FilterBar';
import type { Category, Project } from '@/types';

interface ProjectsClientProps {
  locale: string;
  initialProjects?: Project[];
  /** All active project categories (server-fetched) for the filter chips. */
  categories?: Category[];
  /** Set on a category landing page: only that category's projects are listed. */
  category?: Category | null;
  /** Replaces the generic page heading (category pages use the category's name + description). */
  hero?: ListingHero;
}

export default function ProjectsClient({ locale, initialProjects = [], categories = [], category = null, hero }: ProjectsClientProps) {
  const t        = useTranslations('Projects');

  const sortOptions = [
    { value: '-createdAt', label: t('sortNewest')  },
    { value: 'createdAt',  label: t('sortOldest')  },
    // Titles are bilingual sub-documents: sorting by the English one would order
    // the Arabic listing by English text.
    { value: `-title.${locale}`, label: t('sortTitleAZ') },
    { value: `title.${locale}`,  label: t('sortTitleZA') },
  ];
  const dispatch = useDispatch<AppDispatch>();
  const { items: reduxProjects, loading, total, page, pages, limit, error } = useSelector((s: RootState) => s.projects);
  const { ref, visible } = useIntersection<HTMLElement>();

  // The redux slice is shared with the homepage featured preview — only show its
  // contents once THIS view's own fetch has succeeded, otherwise we flash another
  // page's projects (or a filtered subset) as the initial state.
  const [loaded, setLoaded] = useState(false);

  const [filters, setFilters] = useState<FilterValue>({
    page: 1, limit: 9, sort: '-createdAt',
    // The API resolves the slug (either language) to the category.
    ...(category ? { category: category.slug.en } : {}),
  });

  const projects = loaded ? reduxProjects : initialProjects;

  useEffect(() => {
    let active = true;
    dispatch(fetchActiveProjects(filters)).then(action => {
      if (active && fetchActiveProjects.fulfilled.match(action)) setLoaded(true);
    });
    return () => { active = false; };
  }, [dispatch, filters]);

  const retry = useCallback(() => {
    dispatch(fetchActiveProjects(filters)).then(action => {
      if (fetchActiveProjects.fulfilled.match(action)) setLoaded(true);
    });
  }, [dispatch, filters]);

  const handleFilterChange = useCallback((newFilters: FilterValue) => {
    setFilters(newFilters);
  }, []);

  return (
    <>
      {/* Page hero */}
      <section className="py-16 px-4 sm:px-6 text-center">
        <p className="text-xs font-mono text-primary-500 tracking-widest uppercase mb-3">{hero?.eyebrow ?? t('eyebrow')}</p>
        <h1 className="text-4xl sm:text-5xl font-bold text-(--text) mb-4">{hero?.title ?? t('heading')}</h1>
        {(hero ? hero.description : t('desc')) && (
          <p className="text-lg text-(--muted) max-w-2xl mx-auto">{hero ? hero.description : t('desc')}</p>
        )}
      </section>

      {/* Category filter: links to server-rendered category pages */}
      <CategoryChips locale={locale} section="projects" categories={categories} activeSlug={category?.slug.en} />

      {/* Search / sort */}
      <FilterBar
        sortOptions={sortOptions}
        locale={locale}
        value={filters}
        onChange={handleFilterChange}
        showFeaturedToggle
        featured={!!filters.featured}
        searchPlaceholder={t('searchPlaceholder')}
        // Before this view's own fetch lands, a category page already knows its size from the server.
        total={loaded ? total : category?.count}
      />

      {/* Grid */}
      <section ref={ref} className="section-padding px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">

          {/* Load failure — keep any projects already on screen, offer a retry */}
          {error && (
            <div
              role="alert"
              className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-500/30 bg-red-500/5 px-4 py-3"
            >
              <span className="text-sm text-(--text)">{t('loadError')}</span>
              <button
                type="button"
                onClick={retry}
                disabled={loading}
                className="text-sm font-medium text-primary-500 hover:text-primary-400 transition-colors disabled:opacity-60 cursor-pointer"
              >
                {t('retry')}
              </button>
            </div>
          )}

          {loading && projects.length === 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: filters.limit ?? 9 }).map((_, i) => <SkeletonCard key={i} />)}
            </div>
          ) : projects.length === 0 ? (
            <p className="text-center text-(--muted) py-20">{t('empty')}</p>
          ) : (
            <>
              {/* Keeps the heading order sequential (h1 hero → h2 → h3 cards) for assistive tech. */}
              <h2 className="sr-only">{t('heading')}</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {projects.map((project, i) => (
                  <ProjectCard
                    key={project._id}
                    project={project}
                    locale={locale}
                    labels={{
                      viewProject: t('viewProject'),
                      viewCode:    t('viewCode'),
                      details:     t('details'),
                    }}
                    showFeaturedBadge
                    showCategory
                    priority={i === 0}
                    // First row is above the fold: paint it immediately (don't gate
                    // behind the scroll-reveal) so it isn't excluded from LCP.
                    className={i < 3 ? '' : revealClasses(visible, Math.min(i * 80, 400))}
                  />
                ))}
              </div>

              <div className="mt-8">
                <Pagination
                  page={page}
                  total={total}
                  // Before the first response lands the slice still holds the
                  // server default, which would contradict the per-page control.
                  limit={loaded ? limit : (filters.limit ?? 9)}
                  pages={pages}
                  onPageChange={p => setFilters(f => ({ ...f, page: p }))}
                />
              </div>
            </>
          )}
        </div>
      </section>
    </>
  );
}
